'use client';

import { useMemo, useState, type ReactNode } from 'react';
import type { Data, Layout } from 'plotly.js';
import type { WorkloadMetricsInterval } from '@/lib/urm-api';
import { cn } from '@/lib/utils';
import { MetricsLoadingOverlay } from '@/components/metrics-loading-overlay';
import { POD_PALETTE } from '@/app/rate-limit-metrics/types';
import { PlotlyChart } from '@/app/rate-limit-metrics/components/PlotlyChart';
import {
  buildCollectionHealth,
  buildGroupSummaries,
  buildUserApiHeatmap,
  buildWorkloadShareSeries,
} from '../logic';
import {
  DEFAULT_WORKLOAD_INSIGHT_STATE,
  GROUPING_DIMENSIONS,
  type GroupingDimension,
  type WorkloadGroupSummary,
  type WorkloadInsightState,
} from '../types';
import { WindowAverageRequestTimeChart } from './WindowAverageRequestTimeChart';
import { LatencyStabilityOverview } from './LatencyStabilityOverview';
import { ApiCostRatioOverview } from './ApiCostRatioOverview';
import { PerUserBreakdown } from './PerUserBreakdown';

const PARETO_BUCKET_LIMIT = 15;

function colorForIndex(index: number): string {
  return POD_PALETTE[index % POD_PALETTE.length];
}

function compactAxisLabel(value: string, maximumLength = 30): string {
  if (value.length <= maximumLength) return value;
  return `…${value.slice(-(maximumLength - 1))}`;
}

function heatmapCellStyle(value: number, maximum: number) {
  const intensity = maximum === 0 ? 0 : Math.sqrt(value / maximum);
  return {
    backgroundColor: `hsl(201 88% ${94 - intensity * 50}%)`,
    color: intensity > 0.58 ? 'white' : 'hsl(var(--foreground))',
  };
}

function InsightCard({
  title,
  subtitle,
  className,
  children,
}: {
  title: string;
  subtitle: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={cn('min-w-0 rounded-lg border bg-card px-3 pt-3 pb-1 shadow-sm', className)}>
      <div className="mb-2 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
        <h3 className="text-sm font-semibold">{title}</h3>
        <p className="text-xs text-muted-foreground">{subtitle}</p>
      </div>
      {children}
    </div>
  );
}

function paretoBuckets(summaries: WorkloadGroupSummary[]): WorkloadGroupSummary[] {
  if (summaries.length <= PARETO_BUCKET_LIMIT) return summaries;
  const visible = summaries.slice(0, PARETO_BUCKET_LIMIT - 1);
  const rest = summaries.slice(PARETO_BUCKET_LIMIT - 1);
  const totalProcessingTimeNs = rest.reduce((sum, item) => sum + item.totalProcessingTimeNs, 0);
  const totalRequests = rest.reduce((sum, item) => sum + item.totalRequests, 0);
  return [...visible, {
    key: '__other__',
    label: 'Other',
    groupValues: {},
    totalProcessingTimeNs,
    totalRequests,
    averageDurationMs: totalRequests === 0 ? 0 : totalProcessingTimeNs / totalRequests / 1_000_000,
    processingSharePercent: rest.reduce((sum, item) => sum + item.processingSharePercent, 0),
    requestSharePercent: rest.reduce((sum, item) => sum + item.requestSharePercent, 0),
  }];
}

export function WorkloadInsights({
  intervals,
  loading,
  state: controlledState,
  onStateChange,
}: {
  intervals: WorkloadMetricsInterval[];
  loading: boolean;
  state?: WorkloadInsightState;
  onStateChange?: (state: WorkloadInsightState) => void;
}) {
  const [internalState, setInternalState] = useState<WorkloadInsightState>(() => ({
    dimensions: [...DEFAULT_WORKLOAD_INSIGHT_STATE.dimensions],
    latencyDimensions: [...DEFAULT_WORKLOAD_INSIGHT_STATE.latencyDimensions],
    latencyMetric: DEFAULT_WORKLOAD_INSIGHT_STATE.latencyMetric,
  }));
  const state = controlledState ?? internalState;
  const dimensions = state.dimensions;
  const commitState = (nextState: WorkloadInsightState) => {
    if (controlledState === undefined) setInternalState(nextState);
    onStateChange?.(nextState);
  };
  const dimensionLabel = dimensions
    .map((dimension) => GROUPING_DIMENSIONS.find((item) => item.key === dimension)!.label)
    .join(' × ');
  const summaries = useMemo(
    () => buildGroupSummaries(intervals, dimensions),
    [dimensions, intervals],
  );
  const shareSeries = useMemo(
    () => buildWorkloadShareSeries(intervals, dimensions),
    [dimensions, intervals],
  );
  const heatmap = useMemo(() => buildUserApiHeatmap(intervals), [intervals]);
  const health = useMemo(() => buildCollectionHealth(intervals), [intervals]);
  const toggleDimension = (dimension: GroupingDimension) => {
    let nextDimensions: GroupingDimension[];
    if (dimensions.includes(dimension)) {
      nextDimensions = dimensions.length === 1
        ? dimensions
        : dimensions.filter((item) => item !== dimension);
    } else {
      if (dimensions.length === 2) return;
      const selected = new Set([...dimensions, dimension]);
      nextDimensions = GROUPING_DIMENSIONS
        .map((item) => item.key)
        .filter((key) => selected.has(key));
    }
    commitState({ ...state, dimensions: nextDimensions });
  };

  const shareTraces: Data[] = shareSeries.map((series, index) => ({
    type: 'scatter',
    mode: 'lines',
    name: series.label,
    x: series.timestamps.map((timestamp) => new Date(timestamp)),
    y: series.percentages,
    stackgroup: 'workload-share',
    line: { width: 0.8, color: colorForIndex(index) },
    hovertemplate: `<b>${series.label}</b><br>%{x}<br>%{y:.1f}%<extra></extra>`,
  } as Data));

  const maximumProcessingNs = Math.max(0, ...summaries.map((item) => item.totalProcessingTimeNs));
  const scatterTraces: Data[] = summaries.slice(0, 30).map((item, index) => ({
    type: 'scatter',
    mode: 'markers',
    name: item.label,
    x: [item.totalRequests],
    y: [item.averageDurationMs],
    marker: {
      color: colorForIndex(index),
      opacity: 0.8,
      size: maximumProcessingNs === 0
        ? 10
        : 10 + 24 * Math.sqrt(item.totalProcessingTimeNs / maximumProcessingNs),
      line: { color: 'hsl(var(--background))', width: 1 },
    },
    hovertemplate: `<b>${item.label}</b><br>%{x:,} requests<br>%{y:.1f} ms avg<br>${(item.totalProcessingTimeNs / 1_000_000_000).toLocaleString(undefined, { maximumFractionDigits: 1 })} processing seconds<extra></extra>`,
  } as Data));

  const pareto = paretoBuckets(summaries);
  const paretoIndexes = pareto.map((_, index) => index);
  const paretoLabels = pareto.map((item) => item.label);
  const cumulative = pareto.map((_, index) => pareto
    .slice(0, index + 1)
    .reduce((sum, item) => sum + item.processingSharePercent, 0));
  const paretoTraces: Data[] = [
    {
      type: 'bar',
      name: 'Processing time',
      orientation: 'h',
      x: pareto.map((item) => item.totalProcessingTimeNs / 1_000_000_000),
      y: paretoIndexes,
      customdata: pareto.map((item) => [item.label]),
      marker: { color: pareto.map((_, index) => colorForIndex(index)) },
      hovertemplate: '<b>%{customdata[0]}</b><br>%{x:.1f} processing seconds<extra></extra>',
    } as Data,
    {
      type: 'scatter',
      mode: 'lines+markers',
      name: 'Cumulative share',
      x: cumulative,
      y: paretoIndexes,
      xaxis: 'x2',
      customdata: pareto.map((item) => [item.label]),
      line: { color: '#f59e0b', width: 2 },
      marker: { color: '#f59e0b', size: 5 },
      hovertemplate: '<b>%{customdata[0]}</b><br>%{x:.1f}% cumulative<extra></extra>',
    } as Data,
  ];
  const paretoLayout: Partial<Layout> = {
    margin: { t: 42, r: 24, b: 48, l: 16 },
    bargap: 0.25,
    xaxis: { domain: [0.5, 1] },
    yaxis: {
      tickmode: 'array',
      tickvals: paretoIndexes,
      ticktext: paretoLabels,
      range: [pareto.length - 0.5, -0.5],
      fixedrange: true,
      gridcolor: 'transparent',
      tickfont: { size: 10 },
    },
    xaxis2: {
      title: { text: 'cumulative %', font: { size: 11 } },
      overlaying: 'x',
      side: 'top',
      range: [0, 100],
      fixedrange: true,
      gridcolor: 'transparent',
      zeroline: false,
    },
  };
  const maximumHeatmapSeconds = Math.max(0, ...heatmap.processingSeconds.flat());

  const healthTraces: Data[] = [
    {
      type: 'scatter', mode: 'lines', name: 'Active users',
      x: health.map((point) => new Date(point.timestamp)),
      y: health.map((point) => point.activeUsers),
      line: { color: colorForIndex(0), width: 1.5 },
      hovertemplate: '<b>Active users</b><br>%{x}<br>%{y:.1f}<extra></extra>',
    } as Data,
    {
      type: 'scatter', mode: 'lines', name: 'Aggregates',
      x: health.map((point) => new Date(point.timestamp)),
      y: health.map((point) => point.aggregates),
      line: { color: colorForIndex(1), width: 1.5 },
      hovertemplate: '<b>Aggregates</b><br>%{x}<br>%{y:.1f}<extra></extra>',
    } as Data,
    {
      type: 'scatter', mode: 'lines', name: 'Dropped records',
      x: health.map((point) => new Date(point.timestamp)),
      y: health.map((point) => point.droppedRecords),
      line: { color: '#ef4444', width: 1.5 },
      hovertemplate: '<b>Dropped records</b><br>%{x}<br>%{y:.1f}<extra></extra>',
    } as Data,
  ];

  return (
    <div className="space-y-3">
      <LatencyStabilityOverview intervals={intervals} loading={loading} />

      <ApiCostRatioOverview intervals={intervals} loading={loading} />

      <PerUserBreakdown intervals={intervals} loading={loading} />

      <WindowAverageRequestTimeChart
        intervals={intervals}
        loading={loading}
        dimensions={state.latencyDimensions}
        selectedMetric={state.latencyMetric}
        onStateChange={(latencyDimensions, latencyMetric) => {
          commitState({ ...state, latencyDimensions, latencyMetric });
        }}
      />

      <section aria-label="Analyze-by workload charts" className="space-y-2 rounded-xl border border-primary/20 bg-muted/40 p-2.5 shadow-sm">
        <div className="flex flex-wrap items-center gap-1" role="group" aria-label="Analyze workload insights by">
          <span className="mr-1 text-xs text-muted-foreground">Analyze by</span>
          {GROUPING_DIMENSIONS.map((item) => {
            const selected = dimensions.includes(item.key);
            const disabled = !selected && dimensions.length === 2;
            return (
              <button
                key={item.key}
                type="button"
                aria-pressed={selected}
                disabled={disabled}
                title={disabled ? 'Select at most two dimensions' : undefined}
                onClick={() => toggleDimension(item.key)}
                className={cn(
                  'inline-flex h-6 items-center rounded-md border px-2.5 text-xs transition-colors disabled:cursor-not-allowed disabled:opacity-40',
                  selected
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'bg-background text-muted-foreground hover:bg-muted',
                )}
              >
                {item.label}
              </button>
            );
          })}
        </div>

        <div className="grid min-w-0 grid-cols-1 gap-2 xl:grid-cols-2">
          <InsightCard title="Workload share" subtitle={`Top ${dimensionLabel} groups plus Other`}>
            <PlotlyChart
              traces={shareTraces}
              yTitle="% processing time"
              legendValueLabel="Share %"
              yRange={[0, 100]}
              height={290}
              perPod={false}
              loading={loading}
            />
          </InsightCard>

          <InsightCard title="Volume vs duration" subtitle={`Top 30 ${dimensionLabel} groups; bubble size is processing time`}>
            <PlotlyChart
              traces={scatterTraces}
              xTitle="requests"
              xType="linear"
              yTitle="average duration (ms)"
              height={320}
              perPod={false}
              showLegend={false}
              timeAxis={false}
              loading={loading}
            />
          </InsightCard>

          <InsightCard
            title="Workload Pareto"
            subtitle={`Processing time concentration by ${dimensionLabel}`}
            className="xl:col-span-2"
          >
            <PlotlyChart
              traces={paretoTraces}
              xTitle="processing seconds"
              xType="linear"
              height={Math.max(300, pareto.length * 22 + 90)}
              perPod={false}
              showLegend={false}
              timeAxis={false}
              layoutOverrides={paretoLayout}
              loading={loading}
            />
          </InsightCard>
        </div>
      </section>

      <section aria-label="Cross-dimension and collection charts" className="rounded-xl border border-primary/20 bg-muted/40 p-2.5 shadow-sm">
        <div className="grid min-w-0 grid-cols-1 gap-2">
          <InsightCard title="User × API heatmap" subtitle="Total processing time in the selected window">
          <MetricsLoadingOverlay loading={loading}>
            <div className="overflow-x-auto pb-2">
              <table className="w-full min-w-[760px] table-fixed border-separate border-spacing-0 text-[10px]">
                <colgroup>
                  <col className="w-1/2" />
                  {heatmap.apiLabels.map((apiLabel) => <col key={apiLabel} />)}
                </colgroup>
                <thead>
                  <tr>
                    <th scope="col" className="border-b px-2 py-2 text-left font-medium text-muted-foreground">User / API</th>
                    {heatmap.apiLabels.map((apiLabel) => (
                      <th key={apiLabel} scope="col" className="border-b px-1 py-2 font-mono font-medium text-muted-foreground">
                        <span title={apiLabel} className="block truncate">{compactAxisLabel(apiLabel, 16)}</span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {heatmap.users.map((user, row) => (
                    <tr key={user}>
                      <th scope="row" title={user} className="border-r px-2 py-1 text-right font-mono font-medium">
                        <span className="block truncate">{user}</span>
                      </th>
                      {heatmap.apiLabels.map((apiLabel, column) => {
                        const value = heatmap.processingSeconds[row][column];
                        const formattedValue = value.toLocaleString(undefined, { maximumFractionDigits: 1 });
                        return (
                          <td
                            key={apiLabel}
                            title={`${user} · ${apiLabel}: ${formattedValue} processing seconds`}
                            aria-label={`${user}, ${apiLabel}, ${formattedValue} processing seconds`}
                            className="h-7 border border-background/80 px-0.5 text-center font-mono tabular-nums"
                            style={heatmapCellStyle(value, maximumHeatmapSeconds)}
                          >
                            {value === 0 ? '—' : formattedValue}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-end gap-2 px-2 pb-2 text-[10px] text-muted-foreground">
              <span>0</span>
              <span className="h-2.5 w-28 rounded-sm bg-gradient-to-r from-sky-50 via-sky-400 to-blue-700" aria-hidden="true" />
              <span>{maximumHeatmapSeconds.toLocaleString(undefined, { maximumFractionDigits: 1 })} seconds</span>
            </div>
          </MetricsLoadingOverlay>
          </InsightCard>

          <InsightCard
            title="Collection health"
            subtitle="Distinct users, stored aggregates, and cardinality drops per interval"
          >
            <PlotlyChart
              traces={healthTraces}
              yTitle="count"
              height={260}
              perPod={false}
              loading={loading}
            />
          </InsightCard>
        </div>
      </section>
    </div>
  );
}
